/**
 * dragSort · «разложи карточки по корзинам» перетягиванием (S07 BUG-02).
 * Одобрен Genie 11 JUN 2026 (мокап mockup_s07_dragsort.html).
 *
 * Контракт разметки:
 *   <div class="ds" data-fin="мораль финала">
 *     <div class="ds-head"><span class="ttl">…</span>
 *       <span class="ds-cnt">разложено <b class="ds-num">0</b>/N · промахов <b class="ds-miss">0</b></span></div>
 *     <div class="ds-instr">…</div>
 *     <div class="ds-pool">
 *       <div class="ds-card" data-bin="x" role="button" tabindex="0">Текст ситуации
 *         <span class="grip">⋮⋮</span>
 *         <span class="ds-why">Разбор «почему сюда» — виден в корзине после попадания.</span>
 *       </div>…
 *     </div>
 *     <div class="ds-bins">
 *       <div class="ds-bin" data-bin="x" role="button" tabindex="0">
 *         <div class="bn">ИМЯ <span class="cnt">0</span></div></div>…
 *     </div>
 *     <div class="ds-fin"></div>
 *   </div>
 *
 * Поведение: мышь — настоящий drag (ghost под курсором, корзина подсвечивается,
 * по отпусканию — попадание/отскок). Тач и клавиатура — «тап по карточке →
 * тап по корзине» (drag на таче сознательно выключен: конфликт со скроллом).
 * Верно: карточка переезжает в корзину плашкой + раскрывается ds-why.
 * Мимо: встряска карточки, вспышка корзины, промах в счётчик.
 * Все разложены: ds-fin = data-fin + счёт промахов.
 *
 * No-JS деградация: без инициализации .ds-why видны под карточками (CSS),
 * корзины читаются как список состояний.
 * StrictMode-safe: init-guard сбрасывается в cleanup, DOM восстанавливается.
 */
export function initDragSort(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>('.ds').forEach((el) => {
    if (el.dataset.dsInit === '1') return;
    el.dataset.dsInit = '1';
    const originalHtml = el.innerHTML;
    el.classList.add('ds-ready');

    const pool = el.querySelector<HTMLElement>('.ds-pool');
    const numEl = el.querySelector<HTMLElement>('.ds-num');
    const missEl = el.querySelector<HTMLElement>('.ds-miss');
    const fin = el.querySelector<HTMLElement>('.ds-fin');
    const cards = Array.from(el.querySelectorAll<HTMLElement>('.ds-card'));
    const bins = Array.from(el.querySelectorAll<HTMLElement>('.ds-bin'));
    if (!pool || cards.length === 0 || bins.length === 0) return;

    const total = cards.length;
    let placed = 0;
    let misses = 0;
    let picked: HTMLElement | null = null;

    // drag state (мышь)
    let dragCard: HTMLElement | null = null;
    let ghost: HTMLElement | null = null;
    let started = false;
    let sx = 0;
    let sy = 0;

    const syncCounters = () => {
      if (numEl) numEl.textContent = String(placed);
      if (missEl) missEl.textContent = String(misses);
    };

    const flashMiss = (card: HTMLElement, bin: HTMLElement | null) => {
      misses += 1;
      syncCounters();
      card.classList.add('shake');
      if (bin) bin.classList.add('missflash');
      window.setTimeout(() => {
        card.classList.remove('shake');
        if (bin) bin.classList.remove('missflash');
      }, 420);
    };

    const land = (card: HTMLElement, bin: HTMLElement) => {
      placed += 1;
      const drop = document.createElement('div');
      drop.className = 'drop';
      const why = card.querySelector<HTMLElement>('.ds-why');
      const grip = card.querySelector<HTMLElement>('.grip');
      if (grip) grip.remove();
      const whyText = why ? why.textContent ?? '' : '';
      if (why) why.remove();
      drop.textContent = card.textContent ?? '';
      bin.appendChild(drop);
      if (whyText) {
        const hint = document.createElement('div');
        hint.className = 'hintline';
        hint.textContent = whyText;
        bin.appendChild(hint);
      }
      const cnt = bin.querySelector<HTMLElement>('.bn .cnt');
      if (cnt) cnt.textContent = String(bin.querySelectorAll('.drop').length);
      card.remove();
      syncCounters();
      if (placed === total && fin) {
        const missNote = misses > 0 ? ` Промахов: ${misses} — вернись к парам, где ошиблись руки.` : ' Без единого промаха.';
        fin.textContent = (el.dataset.fin ?? 'Все карточки разложены.') + missNote;
        fin.classList.add('show');
        el.classList.add('complete');
      }
    };

    const resolve = (card: HTMLElement, bin: HTMLElement) => {
      if (card.dataset.bin === bin.dataset.bin) land(card, bin);
      else flashMiss(card, bin);
    };

    const clearPick = () => {
      if (picked) picked.classList.remove('picked');
      picked = null;
    };

    // ── тап-тап (тач + клавиатура + мышь-клик) ──
    const onClick = (ev: Event) => {
      const t = ev.target as HTMLElement;
      const card = t.closest<HTMLElement>('.ds-card');
      const bin = t.closest<HTMLElement>('.ds-bin');
      if (card && pool.contains(card)) {
        if (picked === card) { clearPick(); return; }
        clearPick();
        picked = card;
        card.classList.add('picked');
        return;
      }
      if (bin && picked) {
        const c = picked;
        clearPick();
        resolve(c, bin);
      }
    };

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      const t = ev.target as HTMLElement;
      if (t.closest('.ds-card') || t.closest('.ds-bin')) {
        ev.preventDefault();
        onClick(ev);
      }
    };

    // ── настоящий drag · только мышь ──
    const onPointerDown = (ev: PointerEvent) => {
      if (ev.pointerType !== 'mouse' || ev.button !== 0) return;
      const card = (ev.target as HTMLElement).closest<HTMLElement>('.ds-card');
      if (!card || !pool.contains(card)) return;
      dragCard = card;
      started = false;
      sx = ev.clientX;
      sy = ev.clientY;
    };

    const binUnder = (x: number, y: number): HTMLElement | null => {
      const hit = document.elementFromPoint(x, y);
      return hit ? (hit.closest<HTMLElement>('.ds-bin')) : null;
    };

    const onPointerMove = (ev: PointerEvent) => {
      if (!dragCard) return;
      if (!started) {
        if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < 6) return;
        started = true;
        clearPick();
        ghost = dragCard.cloneNode(true) as HTMLElement;
        ghost.classList.add('ghost');
        const r = dragCard.getBoundingClientRect();
        ghost.style.width = `${r.width}px`;
        ghost.style.position = 'fixed';
        ghost.style.pointerEvents = 'none';
        ghost.style.zIndex = '60';
        document.body.appendChild(ghost);
        dragCard.classList.add('lift');
      }
      if (ghost) {
        ghost.style.left = `${ev.clientX + 10}px`;
        ghost.style.top = `${ev.clientY + 8}px`;
      }
      bins.forEach((b) => b.classList.remove('over'));
      const b = binUnder(ev.clientX, ev.clientY);
      if (b) b.classList.add('over');
    };

    const endDrag = (ev: PointerEvent) => {
      if (!dragCard) return;
      const card = dragCard;
      const wasDrag = started;
      dragCard = null;
      started = false;
      if (ghost) { ghost.remove(); ghost = null; }
      card.classList.remove('lift');
      bins.forEach((b) => b.classList.remove('over'));
      if (!wasDrag) return; // обычный клик — обработает onClick
      const b = binUnder(ev.clientX, ev.clientY);
      if (b) resolve(card, b);
    };

    el.addEventListener('click', onClick);
    el.addEventListener('keydown', onKey);
    el.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', endDrag);

    cleanups.push(() => {
      el.removeEventListener('click', onClick);
      el.removeEventListener('keydown', onKey);
      el.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', endDrag);
      if (ghost) ghost.remove();
      el.innerHTML = originalHtml;
      el.classList.remove('ds-ready', 'complete');
      delete el.dataset.dsInit;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
